import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { DataTable, dataTableColumnHelper } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { Input } from '@/components/ui/input';
import { adminApi, adminKeys, type AdminAuditEntry } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';

const PER_PAGE = 50;
const col = dataTableColumnHelper<AdminAuditEntry>();

const columns = [
  col.accessor('createdAt', { header: 'When', cell: (info) => formatDateTime(info.getValue()) }),
  col.accessor('action', {
    header: 'Action',
    cell: (info) => <span className="font-mono text-micro">{info.getValue()}</span>,
  }),
  col.display({
    id: 'actor',
    header: 'Actor',
    cell: ({ row }) => `${row.original.actorLabel ?? 'system'} (${row.original.actorType})`,
  }),
  col.display({
    id: 'resource',
    header: 'Resource',
    cell: ({ row }) =>
      row.original.resourceType
        ? `${row.original.resourceType} ${row.original.resourceId?.slice(0, 8) ?? ''}`
        : '—',
  }),
  col.accessor('ip', { header: 'IP', cell: (info) => info.getValue() ?? '—' }),
];

function AuditPage() {
  const { page, action } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [draft, setDraft] = useState(action ?? '');
  const params = { page, perPage: PER_PAGE, ...(action ? { action } : {}) };
  const query = useQuery({
    queryKey: adminKeys.audit(params),
    queryFn: () => adminApi.auditLogs(params),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="Audit log"
        description="Every security-relevant and administrative event across all organisations. Secrets and raw licence keys are never recorded."
      />
      <form
        role="search"
        className="max-w-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void navigate({ search: { page: 1, action: draft.trim() || undefined } });
        }}
      >
        <Input
          type="search"
          aria-label="Filter by action"
          placeholder="Filter by action, e.g. admin.licence"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </form>
      <DataTable
        caption="Platform audit log"
        columns={columns}
        data={query.data?.items ?? []}
        isLoading={query.isPending}
        error={query.isError ? messageForError(query.error) : undefined}
        getRowId={(row) => row.id}
        pagination={{
          page,
          perPage: PER_PAGE,
          onPageChange: (next) => void navigate({ search: { page: next, action } }),
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/audit')({
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).catch(1).default(1),
    action: z.string().optional(),
  }),
  component: AuditPage,
});
